import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { createCountStore } from '@stores/create-count-store';

import { useOrderedQueryResults } from 'hooks/use-ordered-query-results';

import {
  addUserFavorite,
  deleteUserFavorite,
  loadUserFavoriteList,
  UserFavorite,
} from './requests';

const favoritesQueryKey = ['favorites'];

export const useInvalidateFavorites = () => {
  const queryClient = useQueryClient();
  const invalidateFavorites = () => queryClient.invalidateQueries(favoritesQueryKey);

  return { invalidateFavorites };
};

const cacheStore = createCountStore();

const NO_FAVORITES: UserFavorite[] = [];

export const useCachedFavorites = () => {
  cacheStore.useStore();
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(favoritesQueryKey) as ReturnType<
    typeof useFavorites
  >['data'];

  return data ?? NO_FAVORITES;
};

export const useFavorites = () => {
  return useQuery({
    queryKey: favoritesQueryKey,
    queryFn: async () => {
      const favorites = await loadUserFavoriteList();
      setTimeout(cacheStore.increase);
      return favorites;
    },
  });
};

export const useFavoritesOrdered = () => {
  const orderedFavorites = useOrderedQueryResults({
    userOptionKey: 'navbarFavoritesOrder',
    useQueryHook: useFavorites,
  });

  return orderedFavorites;
};

export const useAddFavorite = () => {
  const { invalidateFavorites } = useInvalidateFavorites();

  return useMutation(addUserFavorite, {
    onSuccess: invalidateFavorites,
  });
};

export const useRemoveFavorite = () => {
  const { invalidateFavorites } = useInvalidateFavorites();

  return useMutation(deleteUserFavorite, {
    onSuccess: invalidateFavorites,
  });
};
